import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '../../components/EmptyState';
import { SidePanel } from '../../components/SidePanel';
import { useDefaultWeek, usePeriodMutations } from '../../planner/api';
import { PeriodForm } from '../../planner/PeriodForm';
import { periodErrorText } from '../../planner/periodError';

export default function PeriodsPanel() {
  const week = useDefaultWeek();
  const { create, update, remove } = usePeriodMutations();
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <SidePanel title="Tijdvakken" description="Je standaardtijdvakken. Ze mogen niet overlappen; aansluiten mag. Een pauze heeft geen lessen.">
      {week.isPending ? (
        <p>Laden…</p>
      ) : week.isError ? (
        <p role="alert">De tijdvakken konden niet worden geladen. Vernieuw de pagina.</p>
      ) : week.data.periods.length === 0 ? (
        <EmptyState title="Nog geen tijdvakken" note="Voeg je eerste tijdvak toe, bijvoorbeeld Les 1 van 08:30 tot 09:30." />
      ) : (
        <ul className="flex flex-col divide-y">
          {week.data.periods.map((period) => (
            <li key={period.id} className="flex flex-col gap-2 py-2">
              {editingId === period.id ? (
                <>
                  <PeriodForm
                    initial={period}
                    submitLabel="Opslaan"
                    pending={update.isPending}
                    onSubmit={(input) => update.mutate({ id: period.id, ...input }, { onSuccess: () => setEditingId(null) })}
                    onCancel={() => setEditingId(null)}
                  />
                  {update.error && (
                    <p role="alert" className="text-sm">
                      {periodErrorText(update.error)}
                    </p>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{period.label}</span>
                  <span className="tabular-nums">
                    {period.startTime}–{period.endTime}
                  </span>
                  {period.isBreak && <span className="text-sm opacity-70">pauze</span>}
                  <span className="flex-1" />
                  <Button variant="ghost" className="min-h-11" onClick={() => setEditingId(period.id)}>
                    Bewerken
                  </Button>
                  <Button variant="ghost" className="min-h-11" aria-label={`Verwijder ${period.label}`} onClick={() => remove.mutate(period.id)}>
                    Verwijderen
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {remove.error && (
        <p role="alert" className="text-sm">
          {periodErrorText(remove.error)}
        </p>
      )}

      <section aria-labelledby="tijdvak-toevoegen" className="flex flex-col gap-2 border-t pt-4">
        <h2 id="tijdvak-toevoegen" className="text-lg font-bold">
          Tijdvak toevoegen
        </h2>
        <PeriodForm submitLabel="Toevoegen" pending={create.isPending} onSubmit={(input, reset) => create.mutate(input, { onSuccess: reset })} />
        {create.error && (
          <p role="alert" className="text-sm">
            {periodErrorText(create.error)}
          </p>
        )}
      </section>
    </SidePanel>
  );
}
